import { useCallback } from 'react';
import { useCompanySettings } from '../context/CompanySettingsContext';
import { 
  formatDateWithTimezone, 
  formatDateOnly, 
  formatDateTime, 
  getRelativeTime,
  isToday,
  isPast
} from '../utils/dateUtils';

/**
 * Hook that provides date formatting functions using the company's timezone settings
 * 
 * Usage:
 * const { formatDate, formatDateAndTime, formatRelative } = useDateFormat();
 * 
 * // Format a date-only field (like LFD, Appointment Date)
 * formatDate('2025-03-20'); // "03/20/2025" (respects company date format)
 * 
 * // Format a datetime field (like created_at, updated_at)
 * formatDateAndTime('2025-03-20T15:30:00Z'); // "03/20/2025 08:30 AM" (respects timezone)
 * 
 * // Get relative time
 * formatRelative('2025-03-20T15:30:00Z'); // "2 hours ago"
 */
export function useDateFormat() {
  const { timezone, dateFormat } = useCompanySettings();

  /**
   * Format a date-only string (no timezone conversion)
   * Use for fields like LFD, Appointment Date that don't have time components
   */
  const formatDate = useCallback((dateStr) => {
    return formatDateOnly(dateStr, dateFormat);
  }, [dateFormat]);

  /**
   * Format a datetime string with timezone conversion
   * Use for fields like created_at, updated_at, status_date
   */
  const formatDateAndTime = useCallback((dateInput) => {
    return formatDateTime(dateInput, timezone, dateFormat);
  }, [timezone, dateFormat]);

  /**
   * Format a datetime but show only date portion (with timezone conversion)
   * Use when you need date from a timestamp but don't want to show time
   */
  const formatDateFromTimestamp = useCallback((dateInput) => {
    return formatDateWithTimezone(dateInput, timezone, dateFormat, false);
  }, [timezone, dateFormat]);

  /**
   * Get relative time string (e.g., "2 hours ago")
   */
  const formatRelative = useCallback((dateInput) => {
    return getRelativeTime(dateInput, timezone);
  }, [timezone]);

  /**
   * Check if date is today in user's timezone
   */
  const checkIsToday = useCallback((dateInput) => {
    return isToday(dateInput, timezone);
  }, [timezone]);

  /**
   * Check if date is in the past
   */
  const checkIsPast = useCallback((dateInput) => {
    return isPast(dateInput, timezone);
  }, [timezone]);

  return {
    formatDate,
    formatDateAndTime,
    formatDateFromTimestamp,
    formatRelative,
    isToday: checkIsToday,
    isPast: checkIsPast,
    // Expose raw settings for components that need them
    timezone,
    dateFormat
  };
}

export default useDateFormat;
